import type { UseQueryReturn } from "@pinia/colada";
import type { ComponentPropsOptions, ComputedRef, ExtractPublicPropTypes, InjectionKey } from "vue";
import { computed, defineComponent, inject, provide, ref, watchEffect } from "vue";
import Spinner from "@/components/Spinner";
import TeaLoader from "@/components/TeaLoader";
import Result from "@/components/ui/result/Result";
import { useCompoundSlots } from "@/composables/compound-slots";
import { useCtx } from "@/composables/context";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Button from "../button/Button";

/**
 * Error information for a failed query
 */
export interface QueryLoaderError {
  query: UseQueryReturn<unknown>;
  error: Error;
  index: number;
}

/**
 * Context for error information (can be injected by custom error components)
 */
export const queryLoaderErrorCtx = useCtx<{
  errors: ComputedRef<QueryLoaderError[]>;
  retry: () => void;
}>();

/**
 * Compound slots for QueryLoader
 *
 * IMPORTANT: Each QueryLoader instance must have its own isolated slot registry.
 * We create a unique injection key per instance to prevent slot sharing between
 * multiple QueryLoaders on the same page.
 */
type QueryLoaderSlotKey = "$loading" | "$error";

// Unique injection key for QueryLoader instances
const QUERY_LOADER_SLOTS_KEY = Symbol("queryLoaderSlots") as InjectionKey<
  ReturnType<typeof useCompoundSlots<QueryLoaderSlotKey>>
>;

const props = {
  /**
   * Array of Pinia Colada queries to monitor
   */
  queries: p.$array<UseQueryReturn<unknown>>(),

  /**
   * Loading mode:
   * - 'all': Wait for ALL queries to have data (default)
   * - 'any': Render when ANY query has data
   * - 'some': Wait for N queries (minQueries prop)
   */
  mode: p.string<"all" | "any" | "some">("all"),

  /**
   * Minimum number of queries required when mode is 'some'
   */
  minQueries: p.number(1),

  /**
   * Animation for default loading state
   */
  animation: p.string<"tea" | "spinner">("tea"),

  /**
   * Custom class for loader container
   */
  loaderClass: p.string<string>(),

  /**
   * Show errors even if some queries succeeded
   */
  showPartialErrors: p.boolean(false),
} satisfies ComponentPropsOptions;

type QueryLoaderProps = ExtractPublicPropTypes<typeof props>;

/**
 * QueryLoader - Manages loading states for multiple Pinia Colada queries
 *
 * This component handles loading, error, and success states for one or more
 * Pinia Colada queries, providing a consistent and customizable UX.
 *
 * Features:
 * - Composition API with sub-components (QueryLoader.Loading, QueryLoader.Error)
 * - Multiple loading modes (all, any, some)
 * - Automatic error aggregation
 * - Retry functionality
 * - Type-safe with TypeScript
 *
 */
export const QueryLoader = defineComponent({
  name: "QueryLoader",
  props,
  emits: {
    /**
     * Emitted when queries load data for the first time
     * (including cache hit on component mount)
     */
    load: (_data: unknown[]) => true,

    /**
     * Emitted when queries refetch data
     * (cache invalidation, manual refresh, etc)
     */
    refetch: (_data: unknown[]) => true,
  },

  setup(props, { slots, emit }) {
    // Create instance-scoped compound slots (isolated per QueryLoader instance)
    const queryLoaderSlots = useCompoundSlots<QueryLoaderSlotKey>();
    const { getSlot } = queryLoaderSlots.provide();

    // Provide the slots registry to child components (Loading, Error)
    // Using a unique key ensures each QueryLoader instance has its own registry
    provide(QUERY_LOADER_SLOTS_KEY, queryLoaderSlots);

    // Track which query sets have already fired 'load' event
    const firedQueryKeys = ref(new Set<string>());

    // Aggregate loading states from all queries
    const loadingStates = computed(() =>
      props.queries.map((q, index) => ({
        index,
        query: q,
        isLoading: q.isLoading.value,
        isPending: q.isPending.value,
        state: q.state.value,
        hasData: q.data.value != null,
        error: q.error.value,
      }))
    );

    // Filter out disabled queries (pending but not loading, no data, no error)
    // A disabled query (enabled: false) stays in isPending=true, isLoading=false forever
    const activeLoadingStates = computed(() =>
      loadingStates.value.filter((s) => s.isLoading || s.hasData || s.error != null)
    );

    // Helper to check if queries are ready based on mode
    const checkIfReady = (states: typeof activeLoadingStates.value): boolean => {
      if (states.length === 0) return true;

      if (props.mode === "all") {
        // All active queries must have data and not be in initial loading
        return states.every((s) => s.hasData && !s.isLoading);
      }

      if (props.mode === "any") {
        // At least one query has data and is not loading
        return states.some((s) => s.hasData && !s.isLoading);
      }

      if (props.mode === "some") {
        const readyCount = states.filter((s) => s.hasData && !s.isLoading).length;
        return readyCount >= props.minQueries;
      }

      return false;
    };

    // Watch for successful data loads and emit events
    watchEffect(() => {
      const states = activeLoadingStates.value;
      const isReady = checkIfReady(states);

      if (!isReady) return;

      // Generate unique key for current query set
      const queryKey = props.queries
        .map((q) => {
          // Access the query's internal key
          const key = (q as any).key ?? (q as any).$key;
          return JSON.stringify(key);
        })
        .join("|");

      // Extract data from all queries
      const data = props.queries.map((q) => q.data.value);

      // Decide which event to emit
      if (!firedQueryKeys.value.has(queryKey)) {
        // First time - emit 'load'
        emit("load", data);
        firedQueryKeys.value.add(queryKey);
      } else {
        // Refetch - emit 'refetch'
        emit("refetch", data);
      }
    });

    // Determine if we're still loading based on mode (using active queries only)
    const isLoading = computed(() => {
      const states = activeLoadingStates.value;

      if (states.length === 0) return false;

      if (props.mode === "all") {
        // Wait for all active queries to have data OR finish loading
        return states.some((s) => {
          return s.isPending;
        });
      }

      if (props.mode === "any") {
        // Wait for at least one query to have data
        const hasAnyData = states.some((s) => s.hasData);
        return !hasAnyData && states.some((s) => s.isLoading);
      }

      if (props.mode === "some") {
        const loadedCount = states.filter((s) => s.hasData).length;
        return loadedCount < props.minQueries && states.some((s) => s.isLoading);
      }

      return false;
    });

    // Collect all errors
    const errors = computed(() => {
      return loadingStates.value
        .filter((s) => s.error)
        .map((s) => ({
          query: s.query,
          error: s.error as Error,
          index: s.index,
        }));
    });

    // Determine if we should show errors
    const hasErrors = computed(() => {
      if (props.showPartialErrors) {
        return errors.value.length > 0;
      }
      // Only show errors if ALL queries failed
      return errors.value.length === props.queries.length;
    });

    // Retry all queries
    const retry = () => {
      props.queries.forEach((q) => q.refetch());
    };

    // Provide error context
    queryLoaderErrorCtx.provide({
      errors,
      retry,
    });

    // Render priority: Error > Loading > Content
    return () => {
      // CRITICAL: Always render children first (hidden) to allow slot registration
      // Without this, marker components (QueryLoader.Loading, QueryLoader.Error)
      // never get rendered and can't call createSlot() to register themselves
      const childrenForRegistration = <div style={{ display: "none" }}>{slots.default?.()}</div>;

      // 1. Show errors if present
      if (hasErrors.value && !isLoading.value) {
        const errorSlot = getSlot("$error");
        if (errorSlot) {
          return (
            <>
              {childrenForRegistration}
              {errorSlot}
            </>
          );
        }

        // Default error display
        return (
          <>
            {childrenForRegistration}
            <div class="center h-full w-full grow flex-col items-center justify-center space-y-3 py-20">
              <Result
                status="error"
                title="Error loading data"
                description={
                  errors.value.length === 1
                    ? errors.value[0].error.message
                    : `${errors.value.length} queries failed`
                }
              >
                <Button variant="light" block onClick={retry}>
                  Retry
                </Button>
              </Result>
            </div>
          </>
        );
      }

      // 2. Show loading if queries are loading
      if (isLoading.value) {
        const loadingSlot = getSlot("$loading");
        if (loadingSlot) {
          return (
            <>
              {childrenForRegistration}
              {loadingSlot}
            </>
          );
        }

        // Default loading spinner
        return (
          <>
            {childrenForRegistration}
            <div
              class={cn(
                "flex h-full w-full grow flex-col items-center justify-center space-y-3 py-20",
                props.loaderClass
              )}
            >
              {props.animation === "tea" ? (
                <TeaLoader class="text-muted-foreground dark:opacity-50" />
              ) : (
                <Spinner class="text-brand-9A h-5 w-5" />
              )}
            </div>
          </>
        );
      }

      // 3. Show content when ready (slots already registered from first render)
      return slots.default?.();
    };
  },
});

/**
 * QueryLoader.Loading - Marker component for custom loading state
 *
 * Use this component to provide a custom loading UI. The content of this
 * component will be rendered while queries are loading.
 */
QueryLoader.Loading = defineComponent({
  name: "QueryLoaderLoading",
  setup(_, { slots }) {
    // Inject the parent QueryLoader's slot registry
    const queryLoaderSlots = inject(QUERY_LOADER_SLOTS_KEY)!;
    const { createSlot } = queryLoaderSlots.inject();

    createSlot("$loading", () => <>{slots.default?.()}</>);

    return () => null; // Marker component - rendering handled by parent
  },
});

/**
 * QueryLoader.Error - Marker component for custom error state
 *
 * Use this component to provide a custom error UI.
 * Inject queryLoaderErrorCtx to access error information and retry function.
 */
QueryLoader.Error = defineComponent({
  name: "QueryLoaderError",
  setup(_, { slots }) {
    // Inject the parent QueryLoader's slot registry
    const queryLoaderSlots = inject(QUERY_LOADER_SLOTS_KEY)!;
    const { createSlot } = queryLoaderSlots.inject();

    createSlot("$error", () => <>{slots.default?.()}</>);

    return () => null; // Marker component - rendering handled by parent
  },
});

export default QueryLoader;
export type { QueryLoaderProps };
